import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Avatar } from '@/components/Avatar';
import { Input } from '@/components/Controls/Input';
import { Switch } from '@/components/Controls/Switch';
import { FormField } from '@/components/Form/FormField';
import { useEffect } from 'react';
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { Select } from '@/components/Controls/Select';
import { useGetExistingVehicule } from '@/hooks/vehicule.hooks';
import { useGetGroupsSelectOptions } from '@/hooks/group.hooks';
import {
  useGetCompaniesSelectOptions,
  useListAllCompanies,
} from '@/hooks/company.hooks';
import { VehiculeOwnerType } from '@/api/entities/vehicule';
import { Radio } from '@/components/Controls/Radio';
import { Textarea } from '@/components/Controls/Textarea';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import { FieldError } from '@/type/error.types';
import Loader from '@/components/Loading/Loader';

export const VehiculeInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  control,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const { getGroupsSelectOptions } = useGetGroupsSelectOptions();
  const { getCompaniesSelectOptions } = useGetCompaniesSelectOptions();
  const { data: allCompanies, isLoading: isLoadingAllCompanies } =
    useListAllCompanies();

  /**
   * === Set Value Watchers section ===
   */
  const requestVehiculeCheckValue =
    watch &&
    watch(
      'requestVehiculeCheck' as Path<TSchema>,
      data?.requestVehiculeCheck ?? false,
    );

  const vinValue = watch && watch('vin' as Path<TSchema>);

  const licensePlateNumberValue =
    watch && watch('licensePlateNumber' as Path<TSchema>);

  const ownerTypeValue =
    watch && watch('ownerType' as Path<TSchema>, data?.ownerType ?? '');

  const ownerGroupIdValue =
    watch && watch('ownerGroupId' as Path<TSchema>, data?.ownerGroupId ?? '');

  const ownerCompanyIdValue =
    watch &&
    watch('ownerCompanyId' as Path<TSchema>, data?.ownerCompanyId ?? '');

  const activeValue = watch && watch('active' as Path<TSchema>);
  /**
   * === END OF Set Value Watchers section ===
   */

  const { data: existingVehicule } = useGetExistingVehicule({
    id: data?.id,
    ownerGroupId: ownerGroupIdValue,
    ownerCompanyId: ownerCompanyIdValue,
    vin: vinValue,
    licensePlateNumber: licensePlateNumberValue,
  });

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (vinValue) {
      resetFieldError<TSchema>({ fieldName: 'vin', setError });
    }

    if (licensePlateNumberValue) {
      resetFieldError<TSchema>({
        fieldName: 'licensePlateNumber',
        setError,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vinValue, licensePlateNumberValue]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setValue(
        'requestVehiculeCheck' as Path<TSchema>,
        data.requestVehiculeCheck ?? false,
      );
      setValue('picture' as Path<TSchema>, data.picture ?? '');
      setValue('brand' as Path<TSchema>, data.brand ?? '');
      setValue('model' as Path<TSchema>, data.model ?? '');
      setValue('vin' as Path<TSchema>, data.vin ?? '');
      setValue(
        'licensePlateNumber' as Path<TSchema>,
        data.licensePlateNumber ?? '',
      );
      setValue(
        'yearOfFirstRegistration' as Path<TSchema>,
        data.yearOfFirstRegistration ?? null,
      );
      setValue('mileage' as Path<TSchema>, data.mileage ?? null);
      setValue('ownerType' as Path<TSchema>, data.ownerType ?? '');
      setValue('ownerGroupId' as Path<TSchema>, data.ownerGroupId ?? '');
      setValue('ownerCompanyId' as Path<TSchema>, data.ownerCompanyId ?? '');
      setValue('active' as Path<TSchema>, data.active);
      setValue('comment' as Path<TSchema>, data.comment ?? '');
    }
  }, [setValue, data]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  useEffect(() => {
    if (setValue) {
      if (ownerTypeValue === VehiculeOwnerType.GROUP && !ownerGroupIdValue) {
        setValue(
          'ownerGroupId' as Path<TSchema>,
          '' as PathValue<TSchema, Path<TSchema>>,
        );
      }

      if (
        ownerTypeValue === VehiculeOwnerType.COMPANY &&
        !ownerCompanyIdValue
      ) {
        setValue(
          'ownerCompanyId' as Path<TSchema>,
          '' as PathValue<TSchema, Path<TSchema>>,
        );
      }

      if (ownerTypeValue === VehiculeOwnerType.GROUP) {
        setValue(
          'ownerCompanyId' as Path<TSchema>,
          '' as PathValue<TSchema, Path<TSchema>>,
        );
      }

      if (ownerTypeValue === VehiculeOwnerType.COMPANY) {
        setValue(
          'ownerGroupId' as Path<TSchema>,
          '' as PathValue<TSchema, Path<TSchema>>,
        );
      }
    }
  }, [setValue, ownerTypeValue, ownerCompanyIdValue, ownerGroupIdValue]);

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const vehiculeErrorMessages = existingVehicule?.data?.errorMessages;

    if (vehiculeErrorMessages?.includes(FieldError.VIN_WITH_GROUP_ID_EXISTS)) {
      setError('vin' as Path<TSchema>, {
        message: t('vinAlreadyUsedBySameGroup'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (
      vehiculeErrorMessages?.includes(FieldError.VIN_WITH_COMPANY_ID_EXISTS)
    ) {
      setError('vin' as Path<TSchema>, {
        message: t('vinAlreadyUsedBySameCompany'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (vehiculeErrorMessages?.includes(FieldError.VIN_EXISTS)) {
      setError('vin' as Path<TSchema>, {
        message: t('common:vehiculeVinAlreadyExists'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (
      vehiculeErrorMessages?.includes(
        FieldError.LICENSE_PLATE_NUMBER_WITH_GROUP_ID_EXISTS,
      )
    ) {
      setError('licensePlateNumber' as Path<TSchema>, {
        message: t('licensePlateNumberAlreadyUsedBySameGroup'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (
      vehiculeErrorMessages?.includes(
        FieldError.LICENSE_PLATE_NUMBER_WITH_COMPANY_ID_EXISTS,
      )
    ) {
      setError('licensePlateNumber' as Path<TSchema>, {
        message: t('licensePlateNumberAlreadyUsedBySameCompany'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (
      vehiculeErrorMessages?.includes(FieldError.LICENSE_PLATE_NUMBER_EXISTS)
    ) {
      setError('licensePlateNumber' as Path<TSchema>, {
        message: t('common:vehiculeLicensePlateNumberAlreadyExists'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (allFieldErrorsEmpty(errors)) {
      const ownerChanged =
        (data?.ownerGroupId || null) !== (ownerGroupIdValue || null) ||
        (data?.ownerCompanyId || null) !== (ownerCompanyIdValue || null);

      // A group and a company are "related" when the company belongs to the group
      // (Company.groupId === groupId). Reassigning between related owners is an
      // update on the same vehicle; only unrelated reassignments duplicate it.
      const findCompanyGroupId = (companyId?: string | null) =>
        allCompanies?.data?.find(
          (c: { id: string; groupId?: string | null }) => c?.id === companyId,
        )?.groupId ?? null;

      const ownersRelated =
        (!!data?.ownerGroupId &&
          !!ownerCompanyIdValue &&
          findCompanyGroupId(ownerCompanyIdValue) === data?.ownerGroupId) ||
        (!!data?.ownerCompanyId &&
          !!ownerGroupIdValue &&
          findCompanyGroupId(data?.ownerCompanyId) === ownerGroupIdValue);

      if (
        data &&
        (data?.ownerGroupId || data?.ownerCompanyId) &&
        (ownerGroupIdValue || ownerCompanyIdValue) &&
        ownerChanged &&
        !ownersRelated
      ) {
        setValue(
          'isToAdd' as Path<TSchema>,
          true as PathValue<TSchema, Path<TSchema>>,
        );
      } else {
        setValue(
          'isToAdd' as Path<TSchema>,
          false as PathValue<TSchema, Path<TSchema>>,
        );
      }

      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Card
          title={
            <TitleSection
              text={t('theVehicule')}
              defaultIconClassName="ki-badge"
            />
          }
          footer={
            <Button
              buttonClassName="px-8"
              text={
                submitDisabled ? `${t('common:loading')}...` : t('common:save')
              }
              disabled={submitDisabled}
            />
          }
          bodyClassName="py-8 flex flex-col gap-5"
        >
          <FormField
            text={t('requestInspectionToMyTeam')}
            textWrapperClassName="mt-2 max-md:mt-0"
            containerClassName="mb-2"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
              <Switch
                {...(register &&
                  register('requestVehiculeCheck' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.requestVehiculeCheck?.message as string)
                }
                checked={requestVehiculeCheckValue}
              />
            </div>
          </FormField>
          <FormField
            text={t('common:photo')}
            containerClassName="mb-2 max-md:mb-0"
            textWrapperClassName="mt-1.75"
          >
            <Avatar
              outerContainerClassName="max-md:w-full"
              containerClassName="max-md:mx-auto"
              imageUrl={data?.picture}
              onChange={(url) =>
                setValue?.(
                  'picture' as Path<TSchema>,
                  url as PathValue<TSchema, Path<TSchema>>,
                )
              }
            />
          </FormField>
          <FormField
            text={t('common:brand')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('brand' as Path<TSchema>))}
                errorMessage={errors && (errors.brand?.message as string)}
                placeholder={t('common:brand')}
                type="text"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:model')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('model' as Path<TSchema>))}
                errorMessage={errors && (errors.model?.message as string)}
                placeholder={t('common:model')}
                type="text"
              />
            </div>
          </FormField>
          <FormField text={t('common:vin')} textWrapperClassName="mt-3">
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('vin' as Path<TSchema>))}
                errorMessage={errors && (errors.vin?.message as string)}
                placeholder={t('common:vin')}
                type="text"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:licensePlateNumber')}
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register &&
                  register('licensePlateNumber' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.licensePlateNumber?.message as string)
                }
                placeholder={t('common:licensePlateNumber')}
                type="text"
              />
            </div>
          </FormField>
          <FormField
            text={t('yearOfFirstRegistration')}
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register &&
                  register('yearOfFirstRegistration' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.yearOfFirstRegistration?.message as string)
                }
                placeholder={t('yearOfFirstRegistration')}
                onInput={(e) => {
                  // Enforce to input only 4 positive digits
                  e.currentTarget.value =
                    Number(e.currentTarget.value) > 0
                      ? e.currentTarget.value?.slice(0, 4)
                      : null;
                }}
                type="number"
              />
            </div>
          </FormField>
          <FormField text={t('common:mileage')} textWrapperClassName="mt-3">
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('mileage' as Path<TSchema>))}
                errorMessage={errors && (errors.mileage?.message as string)}
                placeholder={t('common:mileage')}
                onInput={(e) => {
                  // Enforce to input only a positive number
                  e.currentTarget.value =
                    Number(e.currentTarget.value) > 0
                      ? e.currentTarget.value
                      : null;
                }}
                type="number"
              />
            </div>
          </FormField>
          <FormField text={t('common:owner')} textWrapperClassName="mt-3">
            <div className="flex flex-1 flex-wrap items-start h-0 gap-5 mt-3">
              <Radio
                {...(register && register('ownerType' as Path<TSchema>))}
                errorMessage={errors && (errors.ownerType?.message as string)}
                radioListClassName="flex-row gap-6"
                defaultValue={ownerTypeValue}
                items={[
                  {
                    displayedText: t('common:group'),
                    value: VehiculeOwnerType.GROUP,
                  },
                  {
                    displayedText: t('common:company'),
                    value: VehiculeOwnerType.COMPANY,
                  },
                ]}
              />
            </div>
          </FormField>
          {ownerTypeValue === VehiculeOwnerType.GROUP && (
            <FormField
              text={t('common:groupOwner')}
              textWrapperClassName="mt-5"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
                <Controller
                  name="ownerGroupId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      errorMessage={
                        errors && (errors.ownerGroupId?.message as string)
                      }
                      placeholder={t('common:chooseTheGroupOwner')}
                      options={getGroupsSelectOptions()}
                    />
                  )}
                />
              </div>
            </FormField>
          )}
          {ownerTypeValue === VehiculeOwnerType.COMPANY && (
            <FormField
              text={t('common:companyOwner')}
              textWrapperClassName="mt-5"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
                <Controller
                  name="ownerCompanyId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      errorMessage={
                        errors && (errors.ownerCompanyId?.message as string)
                      }
                      placeholder={t('common:chooseTheCompanyOwner')}
                      options={getCompaniesSelectOptions()}
                    />
                  )}
                />
              </div>
            </FormField>
          )}
          <FormField text={t('common:status')} textWrapperClassName="mt-2">
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
              <Switch
                {...(register && register('active' as Path<TSchema>))}
                errorMessage={errors && (errors.active?.message as string)}
                checked={data ? activeValue : true}
              />
            </div>
          </FormField>
          <FormField text={t('common:comment')} textWrapperClassName="mt-5">
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
              <Textarea
                {...(register && register('comment' as Path<TSchema>))}
                errorMessage={errors && (errors.comment?.message as string)}
                placeholder={t('common:comment')}
              />
            </div>
          </FormField>
        </Card>
      </form>
      {isLoadingAllCompanies && <Loader />}
    </>
  );
};
